// The one labelled ad on a living story.
import { formatDate, toLocalInput } from './newsroom.js';

export const SPONSOR_NAME_MAX = 35;
export const SPECIALISM_MAX = 24;
export const SPECIALISMS = 3;
export const LOGO_TYPES = [
    'image/png',
    'image/jpeg',
    'image/svg+xml',
    'image/webp',
];
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const PROOF_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'application/pdf',
];
export const PROOF_MAX_BYTES = 10 * 1024 * 1024;

const time = (value) => (value ? new Date(value).getTime() : NaN);
const text = (value) => (typeof value === 'string' ? value.trim() : '');

// Only an http(s) address becomes an href or an img src, whatever the API sent.
export const httpUrl = (value) =>
    /^https?:\/\//i.test(text(value)) ? text(value) : undefined;

export const cleanSpecialisms = (list) =>
    (Array.isArray(list) ? list : []).map(text).filter(Boolean);

export const sponsorLabel = (verified) =>
    verified
        ? 'Sponsored by Newspage-verified expert'
        : 'Sponsored by Newspage expert';

// The admin story carries the approval and the profile as objects, the public one as booleans.
const isApproved = (story) =>
    story.sponsorApproved ?? Boolean(story.sponsorApproval);
const isVerified = (story) =>
    story.sponsorVerified ?? Boolean(story.sponsorProfile?.isVerified);

export const templateComplete = (story) => {
    const count = cleanSpecialisms(story?.sponsorSpecialisms).length;
    return Boolean(
        text(story?.sponsorName) &&
            text(story?.sponsorLogoUrl) &&
            text(story?.sponsorUrl) &&
            count > 0 &&
            count <= SPECIALISMS
    );
};

// What the banner shows, without the go-live checks: the public page and the admin preview.
export const templateSponsor = (story, verified = isVerified(story)) => ({
    name: text(story.sponsorName),
    url: text(story.sponsorUrl),
    logoUrl: text(story.sponsorLogoUrl),
    logoBg: story.sponsorLogoBg === 'white' ? 'white' : 'navy',
    specialisms: cleanSpecialisms(story.sponsorSpecialisms).slice(
        0,
        SPECIALISMS
    ),
    label: sponsorLabel(verified),
});

// A template banner needs the sponsor's approval. An old image slot never had one,
// so it keeps running as before rather than vanishing on deploy.
export const liveSponsor = (story, now = Date.now()) => {
    if (!story) return null;
    if (time(story.sponsorStartsAt) > now || time(story.sponsorEndsAt) <= now) {
        return null;
    }
    if (templateComplete(story)) {
        return isApproved(story) ? templateSponsor(story) : null;
    }
    const { sponsorName, sponsorImageUrl, sponsorUrl } = story;
    if (!sponsorName || !sponsorImageUrl || !sponsorUrl) {
        return null;
    }
    return { name: sponsorName, imageUrl: sponsorImageUrl, url: sponsorUrl };
};

export const isLegacySlot = (story) =>
    Boolean(text(story?.sponsorImageUrl)) && !templateComplete(story);

// Everything the sponsor signs off: the API clears the approval when any of it changes.
export const sponsorBannerKey = (fields) =>
    JSON.stringify([
        text(fields?.sponsorName),
        text(fields?.sponsorLogoUrl),
        fields?.sponsorLogoBg === 'white' ? 'white' : 'navy',
        cleanSpecialisms(fields?.sponsorSpecialisms),
        text(fields?.sponsorUrl),
        fields?.sponsorProfileId ? String(fields.sponsorProfileId) : '',
        text(fields?.sponsorImageUrl),
    ]);

// The slot's fields in the Storify form; with no story, an empty slot.
export const sponsorDraft = (story) => ({
    sponsorName: story?.sponsorName ?? '',
    sponsorLogoUrl: story?.sponsorLogoUrl ?? '',
    sponsorLogoBg: story?.sponsorLogoBg === 'white' ? 'white' : 'navy',
    sponsorSpecialisms: Array.from(
        { length: SPECIALISMS },
        (_, i) => story?.sponsorSpecialisms?.[i] ?? ''
    ),
    sponsorProfileId: story?.sponsorProfileId
        ? String(story.sponsorProfileId)
        : '',
    // Old image slots only: kept so they still save, no longer offered in the form.
    sponsorImageUrl: story?.sponsorImageUrl ?? '',
    sponsorUrl: story?.sponsorUrl ?? '',
    sponsorStartsAt: toLocalInput(story?.sponsorStartsAt),
    sponsorEndsAt: toLocalInput(story?.sponsorEndsAt),
});

// A logo uploaded and then abandoned is not a sponsor: the save clears it rather
// than blocking every other edit to the story.
export const sponsorStarted = (draft) =>
    [
        draft.sponsorName,
        draft.sponsorUrl,
        draft.sponsorProfileId,
        draft.sponsorImageUrl,
    ].some(text) || cleanSpecialisms(draft.sponsorSpecialisms).length > 0;

// Mirrors the API's check so the editor hears about it before the save.
export const sponsorProblem = (draft) => {
    const specialisms = cleanSpecialisms(draft.sponsorSpecialisms);
    if (!sponsorStarted(draft)) {
        return null;
    }
    if (text(draft.sponsorUrl) && !/^https?:\/\/\S+$/i.test(draft.sponsorUrl)) {
        return 'The sponsor link must start with http:// or https://.';
    }
    // An old image slot still saves as it was, whatever the length of its name.
    if (
        isLegacySlot(draft) &&
        text(draft.sponsorName) &&
        text(draft.sponsorUrl)
    ) {
        return null;
    }
    if (text(draft.sponsorName).length > SPONSOR_NAME_MAX) {
        return `The sponsor name can be ${SPONSOR_NAME_MAX} characters at most.`;
    }
    if (specialisms.some((s) => s.length > SPECIALISM_MAX)) {
        return `Each specialism can be ${SPECIALISM_MAX} characters at most.`;
    }
    if (templateComplete(draft)) {
        return null;
    }
    return 'A sponsor needs a name, a logo, three or fewer specialisms and a link.';
};

export const fileProblem = (file, types, maxBytes, what) => {
    if (!file) return null;
    if (!types.includes(file.type)) {
        return `That file type is not accepted for the ${what}.`;
    }
    if (file.size > maxBytes) {
        return `The ${what} can be ${maxBytes / 1024 / 1024} MB at most.`;
    }
    return null;
};

// "oliver-smith", "/oliver-smith" or any page of the profile, such as
// "https://app.newspage.media/oliver-smith/media-coverage?tab=raws".
export const profileSlug = (input) => {
    const value = text(input);
    let path;
    try {
        path = new URL(value).pathname;
    } catch {
        path = value.split(/[?#]/)[0];
    }
    return path.split('/').filter(Boolean)[0] || '';
};

// The page is ISR: rebuild it when the slot goes up or comes down, not a day later.
export const sponsorRevalidate = (story, revalidate, now = Date.now()) => {
    const next = [time(story?.sponsorStartsAt), time(story?.sponsorEndsAt)]
        .filter((at) => at > now)
        .map((at) => Math.ceil((at - now) / 1000));
    return Math.max(60, Math.min(revalidate, ...next));
};

// Admin list of story banners (/faq-sponsors).
export const STORY_SPONSOR_FILTERS = [
    ['all', 'All'],
    ['waiting', 'Waiting'],
    ['live', 'Live'],
    ['scheduled', 'Scheduled'],
    ['unpublished', 'Unpublished'],
    ['ended', 'Ended'],
];

const STATUS_LABEL = {
    waiting: 'Waiting for sponsor approval',
    scheduled: 'Scheduled',
    live: 'Live',
    unpublished: 'Story not published',
    ended: 'Ended',
};

export const storySponsorStatus = (item) => STATUS_LABEL[item?.status] ?? '—';

export const storySponsorApproved = (item) =>
    item?.approval
        ? `${item.approval.approvedBy?.name || 'An admin'} · ${formatDate(
              item.approval.approvedAt
          )}`
        : '—';

export const storySponsorRuns = ({ sponsorStartsAt, sponsorEndsAt } = {}) => {
    const from = sponsorStartsAt ? formatDate(sponsorStartsAt) : '';
    const until = sponsorEndsAt ? formatDate(sponsorEndsAt) : '';
    if (from && until) return `${from} \u2013 ${until}`;
    if (until) return `Until ${until}`;
    return from ? `From ${from}, no end date` : 'No end date';
};
